/**
 * session.ts — Signing in, and keeping the local save and the account in step.
 *
 * ## The rule this file exists to protect
 *
 * **The local save is the source of truth for playing the game.** Signing in
 * adds a copy that follows you between devices; it never becomes a dependency.
 * Every path here swallows its errors, and there is no state — offline, signed
 * out, misconfigured, Firebase failing to load at all — in which the campaign
 * stops working or the menu stops rendering. That is the same contract
 * `progress.ts` already keeps for `localStorage`, extended over a network.
 *
 * ## Shape
 *
 *   main.ts ──> CloudSave ──> Backend ──> firebase.ts
 *                    ├──> mergeSaves()          (save.ts, pure)
 *                    └──> progress.ts, settings.ts   (the local store)
 *
 * `CloudSave` never imports Firebase. It is handed a `Connector`, which is what
 * makes the whole thing testable against `src/test/fakeBackend.ts` and what lets
 * the SDK load lazily.
 *
 * ## Why it loads lazily
 *
 * The Firebase SDK is heavier than everything else this game ships. A player who
 * has never signed in shouldn't wait for it, so it is fetched on the first click
 * of the sign-in button — and, for someone who *has* signed in before, eagerly
 * at boot, because a returning player expects their ticks to be there without
 * clicking anything. The local flag in `dogwalker.sync` is what tells those two
 * cases apart without loading anything to ask.
 */
import { Emitter } from '../types';
import { clearProgress, completedLevels, setCompleted } from '../ui/progress';
import { mutedSetting, setMutedSetting } from '../ui/settings';
import { SAVE_VERSION, emptySave, mergeSaves, parseSave, type SaveData } from './save';
import type { Backend, CloudUser, Connector } from './backend';

/** Emitted whenever `status`, `user`, or the local save changes. No payload. */
export const CloudEvents = { CHANGED: 'cloud-changed' } as const;

export type CloudStatus =
  /** This build has no Firebase config. Sign-in isn't offered at all. */
  | 'unavailable'
  | 'signed-out'
  | 'signing-in'
  /** Signed in, first merge in flight. */
  | 'syncing'
  | 'synced'
  /** Signed in, but the last write didn't land. Progress is safe locally. */
  | 'offline';

/**
 * The slice of `CloudSave` the menu is allowed to see.
 *
 * `menu.ts` is kept ignorant of `LevelSpec` and of how games start; it should be
 * just as ignorant of Firebase. It gets this, and a stub satisfies it in tests.
 */
export interface CloudSaveHandle {
  readonly events: Emitter;
  readonly status: CloudStatus;
  readonly user: CloudUser | null;
  signIn(): void;
  signOut(): void;
  /** Wipe progress here, and on the account if there is one. */
  resetProgress(): void;
}

/**
 * Local sync bookkeeping. A **separate key** from `dogwalker.completed`, which
 * keeps its bare `number[]` shape: an older build reads a non-array there and
 * throws the lot away (`progress.ts`), so putting anything richer in it would
 * wipe the progress of anyone who opened a cached tab. Nothing to migrate.
 */
const SYNC_KEY = 'dogwalker.sync';

interface SyncMeta {
  /** The account last signed in on this device, or null. Gates the eager load. */
  uid: string | null;
  /** When the local save last changed, for the mute tie-break in `mergeSaves`. */
  updatedAt: number;
  /** A write we owe the account — set when one fails, cleared when one lands. */
  dirty: boolean;
}

const NO_META: SyncMeta = { uid: null, updatedAt: 0, dirty: false };

/**
 * Stands in for a uid while a sign-in is in flight.
 *
 * On a browser where popups are blocked, signing in *navigates away* and comes
 * back a page load later. Without a marker written before that happens, the
 * returning page would see no previous session, never load the SDK, and leave a
 * player who just signed in looking signed out. The real uid replaces this as
 * soon as the auth listener fires.
 */
const PENDING_UID = '(pending)';

function readMeta(): SyncMeta {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(SYNC_KEY) ?? '');
    if (typeof raw !== 'object' || raw === null) return { ...NO_META };
    const meta = raw as Record<string, unknown>;
    return {
      uid: typeof meta.uid === 'string' ? meta.uid : null,
      updatedAt: typeof meta.updatedAt === 'number' && Number.isFinite(meta.updatedAt) ? meta.updatedAt : 0,
      dirty: meta.dirty === true,
    };
  } catch {
    // Missing, unparseable, or storage is off. All mean the same thing here.
    return { ...NO_META };
  }
}

function writeMeta(meta: SyncMeta): void {
  try {
    window.localStorage.setItem(SYNC_KEY, JSON.stringify(meta));
  } catch {
    /* the sync still happened; we just won't remember it next boot */
  }
}

/** Is there an account to reconnect to? Cheap enough to ask before booting the SDK. */
export function hasPreviousSession(): boolean {
  return readMeta().uid !== null;
}

export interface CloudSaveOptions {
  /** Injected for tests; `Date.now` in the game. */
  now?: () => number;
  /** How long to collect changes before writing. Rare events, so this is mostly
   *  insurance against someone drumming on the mute button. */
  debounceMs?: number;
}

export class CloudSave implements CloudSaveHandle {
  readonly events = new Emitter();

  private backend: Backend | null = null;
  private connecting: Promise<Backend | null> | null = null;
  private unwatch: (() => void) | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;

  private state: CloudStatus;
  private who: CloudUser | null = null;

  private readonly now: () => number;
  private readonly debounceMs: number;

  constructor(
    private readonly connector: Connector | null,
    options: CloudSaveOptions = {},
  ) {
    this.now = options.now ?? (() => Date.now());
    this.debounceMs = options.debounceMs ?? 1000;
    this.state = connector ? 'signed-out' : 'unavailable';

    // A returning player gets their session restored without touching anything.
    // Anyone else pays nothing: this is the only eager path to the SDK.
    if (connector && hasPreviousSession()) void this.connect();
  }

  get status(): CloudStatus {
    return this.state;
  }

  get user(): CloudUser | null {
    return this.who;
  }

  // ── What the local device currently knows ────────────────────────────────

  private localSave(): SaveData {
    return {
      version: SAVE_VERSION,
      completed: [...completedLevels()].sort((a, b) => a - b),
      muted: mutedSetting(),
      updatedAt: readMeta().updatedAt,
    };
  }

  private applyLocally(save: SaveData): void {
    setCompleted(save.completed);
    setMutedSetting(save.muted);
  }

  private announce(state: CloudStatus): void {
    this.state = state;
    this.events.emit(CloudEvents.CHANGED);
  }

  // ── Connecting ───────────────────────────────────────────────────────────

  /** Load and connect the backend, at most once. `null` if there isn't one. */
  private connect(): Promise<Backend | null> {
    if (this.backend) return Promise.resolve(this.backend);
    if (!this.connector) return Promise.resolve(null);
    if (this.connecting) return this.connecting;

    this.connecting = this.connector()
      .then((backend) => {
        this.backend = backend;
        this.unwatch = backend.onUser((user) => this.onUser(user));
        return backend;
      })
      .catch(() => {
        // A failed chunk fetch or a misconfigured project. Stay signed-out
        // rather than 'unavailable' so the button is still there to retry with.
        this.connecting = null;
        return null;
      });

    return this.connecting;
  }

  private onUser(user: CloudUser | null): void {
    this.who = user;
    if (!user) {
      writeMeta({ ...readMeta(), uid: null });
      this.announce('signed-out');
      return;
    }
    this.announce('syncing');
    void this.sync(user);
  }

  // ── The merge ────────────────────────────────────────────────────────────

  /**
   * Combine this device's save with the account's, then write the result to
   * both. Runs on every sign-in, including the silent one at boot.
   *
   * Local goes in first so a tie on `updatedAt` keeps the setting the player can
   * currently see, rather than flipping it under them.
   */
  private async sync(user: CloudUser): Promise<void> {
    const backend = this.backend;
    if (!backend) return;

    try {
      const remote = parseSave(await backend.load(user.uid)) ?? emptySave();
      const merged = mergeSaves(this.localSave(), remote);

      this.applyLocally(merged);
      await backend.store(user.uid, merged);

      writeMeta({ uid: user.uid, updatedAt: merged.updatedAt, dirty: false });
      this.announce('synced');
    } catch {
      // Offline, or rules said no. The merge that matters — the player's own
      // ticks — is already on this device; we owe the account a write.
      writeMeta({ ...readMeta(), uid: user.uid, dirty: true });
      this.announce('offline');
    }
  }

  // ── Writing ──────────────────────────────────────────────────────────────

  /**
   * Note that the local save changed and schedule a write.
   *
   * Called after a win and after a mute toggle. Always stamps the local
   * timestamp, signed in or not, so that whenever an account does turn up the
   * tie-break in `mergeSaves` has something honest to compare.
   */
  push(): void {
    writeMeta({ ...readMeta(), updatedAt: this.now() });
    if (!this.who) return;

    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.debounceMs);
  }

  /** Write the local save to the account now. No-op when signed out. */
  async flush(): Promise<void> {
    clearTimeout(this.timer);
    this.timer = undefined;

    const backend = this.backend;
    const user = this.who;
    if (!backend || !user) return;

    const save = this.localSave();
    try {
      await backend.store(user.uid, save);
      writeMeta({ uid: user.uid, updatedAt: save.updatedAt, dirty: false });
      this.announce('synced');
    } catch {
      writeMeta({ uid: user.uid, updatedAt: save.updatedAt, dirty: true });
      this.announce('offline');
    }
  }

  // ── What the menu calls ──────────────────────────────────────────────────

  signIn(): void {
    if (this.state === 'signing-in' || this.who) return;
    this.announce('signing-in');

    void this.connect().then(async (backend) => {
      if (!backend) {
        this.announce('signed-out');
        return;
      }
      // Recorded *before* the attempt, because a browser that blocks popups
      // signs in by navigating away — see PENDING_UID.
      writeMeta({ ...readMeta(), uid: PENDING_UID });
      try {
        await backend.signIn();
        // Nothing to do on success: `onUser` fires and takes it from here.
      } catch {
        // A closed popup, a blocked one, a cancelled flow. Not worth a message —
        // the player just did it, and the button is right there.
        if (!this.who) {
          writeMeta({ ...readMeta(), uid: null });
          this.announce('signed-out');
        }
      }
    });
  }

  signOut(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    // Drop the flag first, so a failed sign-out can't leave the next boot
    // eagerly loading the SDK for a session that's gone.
    writeMeta({ ...readMeta(), uid: null });
    void this.backend?.signOut().catch(() => {
      /* already forgotten locally; the token expires on its own */
    });
    this.who = null;
    this.announce('signed-out');
  }

  /**
   * Wipe progress here and on the account.
   *
   * Clearing only the local copy would be a trap: the next sync would union the
   * empty device with the full account and put every tick straight back.
   */
  resetProgress(): void {
    clearProgress();
    const stamp = this.now();
    writeMeta({ ...readMeta(), updatedAt: stamp });

    const backend = this.backend;
    const user = this.who;
    if (!backend || !user) return;

    void backend
      .store(user.uid, { ...emptySave(), muted: mutedSetting(), updatedAt: stamp })
      .then(() => writeMeta({ uid: user.uid, updatedAt: stamp, dirty: false }))
      .catch(() => {
        writeMeta({ uid: user.uid, updatedAt: stamp, dirty: true });
        this.announce('offline');
      });
  }

  dispose(): void {
    clearTimeout(this.timer);
    this.unwatch?.();
    this.events.removeAll();
  }
}
